import { DefaultBlockRegistry, DEFAULT_BLOCKS } from "../sim/world/registry";
import type { BlockDef } from "../sim/world/types";

/** Stable ids shared by deterministic generation, interaction, and rendering. */
export const WORLDGEN_BLOCK_ID = Object.freeze({
  sand: 6,
  plainsGrass: 7,
  oakLog: 8,
  oakLeaves: 9,
  forestGroundCover: 10,
  grassBlade: 11,
  flowerViolet: 12,
  flowerRed: 13,
  flowerOrange: 14,
} as const);

/**
 * `opaque` here means "hides the face behind it", which at micro-voxel scale is
 * true of every vegetation block: a leaf is a solid flat-coloured 12.5 cm cube,
 * not an alpha cutout. Marking crowns opaque is what lets greedy meshing drop
 * the interior of a hundred-thousand-voxel canopy.
 *
 * `solid` stays false for grass, flowers and leaves so the player walks
 * *into* ground cover rather than onto it (docs/ART-DIRECTION.md §1.4).
 */
const NATURAL_BLOCKS: readonly BlockDef[] = Object.freeze([
  Object.freeze({
    id: WORLDGEN_BLOCK_ID.sand,
    name: "sand",
    solid: true,
    opaque: true,
    hardness: 0.5,
    toolTier: 0,
    emission: 0,
  }),
  Object.freeze({
    id: WORLDGEN_BLOCK_ID.plainsGrass,
    name: "plains_grass",
    solid: true,
    opaque: true,
    hardness: 0.6,
    toolTier: 0,
    emission: 0,
  }),
  Object.freeze({
    id: WORLDGEN_BLOCK_ID.oakLog,
    name: "oak_log",
    solid: true,
    opaque: true,
    hardness: 2,
    toolTier: 0,
    emission: 0,
  }),
  Object.freeze({
    id: WORLDGEN_BLOCK_ID.oakLeaves,
    name: "oak_leaves",
    solid: false,
    opaque: true,
    hardness: 0.2,
    toolTier: 0,
    emission: 0,
  }),
  Object.freeze({
    id: WORLDGEN_BLOCK_ID.forestGroundCover,
    name: "forest_ground_cover",
    solid: false,
    opaque: true,
    hardness: 0.1,
    toolTier: 0,
    emission: 0,
  }),
  Object.freeze({
    id: WORLDGEN_BLOCK_ID.grassBlade,
    name: "grass_blade",
    solid: false,
    opaque: true,
    hardness: 0.1,
    toolTier: 0,
    emission: 0,
  }),
  Object.freeze({
    id: WORLDGEN_BLOCK_ID.flowerViolet,
    name: "flower_violet",
    solid: false,
    opaque: true,
    hardness: 0.1,
    toolTier: 0,
    emission: 0,
  }),
  Object.freeze({
    id: WORLDGEN_BLOCK_ID.flowerRed,
    name: "flower_red",
    solid: false,
    opaque: true,
    hardness: 0.1,
    toolTier: 0,
    emission: 0,
  }),
  Object.freeze({
    id: WORLDGEN_BLOCK_ID.flowerOrange,
    name: "flower_orange",
    solid: false,
    opaque: true,
    hardness: 0.1,
    toolTier: 0,
    emission: 0,
  }),
]);

/**
 * The runtime registry includes every id emitted by `generate`. Keeping the
 * catalog here makes world generation independently testable while ensuring
 * collision, edits, and meshing all agree on the same natural blocks.
 */
export const WORLDGEN_BLOCKS = new DefaultBlockRegistry([
  ...DEFAULT_BLOCKS.all,
  ...NATURAL_BLOCKS,
]);
